import React, { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { useFetchConfig } from '@/components/ConfigsProvider';
import AIMessageInput from '@/components/LyearnAI/components/Editor/components/AIMessageInput';
import { aiActionButtonTextStyles } from '@/components/LyearnAI/components/GradientText/styles';
import RichTextViewer from '@/components/RichTextViewer';
import ScoreBox from '@/modules/corporate/modules/scorecards/components/Skill/components/ScoreBox';
import { TextUseCaseEnum } from '@/types/schema';

import ScoreLabels from '../SelectedScoreLabel';
import SkillRemoveButton from '../SkillRemoveButton';
import SkillTooltip from '../SkillTooltip';
import { ReviewCardDetailProps } from './types';

const EDITOR_DEBOUNCE_TIME = 400;

function ReviewCardListItem(props: ReviewCardDetailProps) {
  const { t } = useTranslation(['Skills', 'AI']);
  const {
    skill,
    className,
    onChange,
    score,
    feedback,
    removeSkill,
    isDisabled = false,
    isPreview = false,
    showScoreFooter = true,
    showScoreLabel = true,
    isMindset = false,
    skillNameVariant = 'body-short-01',
    skillNameClassname = 'mb-12',
    skillExpectedScore,
    isRepeatedSkill,
  } = props;
  const { id, name, maxScore, rubrics } = skill;

  const [feedbackText, setFeedbackNote] = useState(feedback ?? '');
  const [isNoteNudged, setIsNoteNudged] = useState(false);
  const { skillRubricConfigs } = useFetchConfig(['skillRubricConfigs']);

  const _onChange: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      setIsNoteNudged(false);

      const value = event.currentTarget.getAttribute('data-value');
      if (!value) {
        return;
      }
      onChange?.({
        id,
        score: parseInt(value, 10),
        feedback: feedbackText,
      });
    },
    [feedbackText, id, onChange],
  );

  useDebounce(
    () => {
      setIsNoteNudged(false);

      if (score === undefined) {
        return;
      }

      if (feedbackText === (feedback ?? '')) {
        // editor onChange is called on editor mount,
        // which sends submitSkillReview request for every skill initially
        // so we need to ignore these calls
        return;
      }

      onChange?.({ id, score, feedback: feedbackText });
    },
    EDITOR_DEBOUNCE_TIME,
    [feedbackText],
  );

  const selectedRubric = useMemo(
    () => rubrics?.find((rubric) => rubric.score === score),
    [rubrics, score],
  );

  return (
    <Stack alignItems="stretch" className={className} direction="col" justifyContent="center">
      <Stack
        alignItems="center"
        className={cn('w-full', showScoreFooter ? skillNameClassname : '')}
        justifyContent="between">
        <Stack alignItems="center" className="w-full">
          <Typography className="px-8" color="text-text-paragraph" variant={skillNameVariant}>
            {name}
          </Typography>
          <SkillTooltip skill={skill} />
        </Stack>
        {removeSkill ? (
          <SkillRemoveButton isMindset={isMindset} onRemove={() => removeSkill(id)} />
        ) : null}
      </Stack>
      {showScoreFooter ? (
        <>
          <ScoreBox
            disabled={isDisabled || isPreview}
            expectedScore={skillExpectedScore}
            isRepeatedSkill={isRepeatedSkill}
            maxScore={maxScore ?? 5}
            score={score}
            onChange={_onChange}
          />
          {showScoreLabel ? (
            <>
              {selectedRubric ? <ScoreLabels rubric={selectedRubric} /> : null}
              {isPreview ? (
                feedback ? (
                  <Box className="mt-24 rounded-8 bg-bg-secondary p-12 ps-16">
                    <RichTextViewer
                      as="div"
                      color="text-text-primary"
                      html={feedback}
                      variant="body-short-02"
                    />
                  </Box>
                ) : null
              ) : (
                <>
                  <AIMessageInput
                    className="mt-24"
                    disabled={isDisabled}
                    error={isNoteNudged}
                    placeholder={t('Skills:Review.AddNote')}
                    textUseCase={TextUseCaseEnum.PerformanceFeedback}
                    topic={feedbackText}
                    value={feedbackText}
                    onChange={setFeedbackNote}
                    onTopicError={() => {
                      setIsNoteNudged(true);
                    }}
                  />
                  {isNoteNudged ? (
                    <Typography
                      className={cn('mt-8 ms-8', aiActionButtonTextStyles)}
                      variant="body-short-03">
                      {t('AI:WriteAWordForAiToWork')}
                    </Typography>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <Stack className="mt-8 w-full" justifyContent="between">
              <Typography className="ms-8" color="text-text-secondary" variant="body-short-03">
                {skillRubricConfigs?.[0].label}
              </Typography>
              <Typography className="me-8" color="text-text-secondary" variant="body-short-03">
                {skillRubricConfigs?.[skillRubricConfigs.length - 1].label}
              </Typography>
            </Stack>
          )}
        </>
      ) : null}
    </Stack>
  );
}

export default React.memo(ReviewCardListItem);
